'use client'

import React, { useEffect, useState } from 'react'

import { throttle } from 'throttle-debounce'

export default function AnchoredContentSidebar({ sections, componentRef, sectionRefs }) {
  // Tracks the active section
  const [activeSectionId, setActiveSectionId] = useState('')

  // A function that will set the selected section based on the scroll position
  const handleScroll = throttle(50, () => {
    if (!sectionRefs.current.length) {
      return
    }

    const firstSection = sectionRefs.current[0]
    const lastSection = sectionRefs.current[sectionRefs.current.length - 1]

    if (!firstSection || !lastSection) {
      return
    }

    const scrollPosition = window.scrollY + window.innerHeight / 3

    // If we're above the first section, set the selected section to the first section
    if (scrollPosition < firstSection.offsetTop) {
      setActiveSectionId(firstSection.dataset.anchorId)
      return
    }

    // If we're below the last section, set the selected section to the last section
    if (scrollPosition > lastSection.offsetTop + lastSection.offsetHeight) {
      setActiveSectionId(lastSection.dataset.anchorId)
      return
    }

    // Loop through each section and check if the scroll position is within the section
    for (let index = 0; index < sectionRefs.current.length; index++) {
      const section = sectionRefs.current[index]

      if (!section) {
        continue
      }

      const sectionTop = section.offsetTop
      const sectionHeight = section.offsetHeight
      const sectionBottom = sectionTop + sectionHeight

      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        setActiveSectionId(section.dataset.anchorId)
        break
      }
    }
  })

  // Add scroll event listener when the component mounts
  useEffect(() => {
    if (!sectionRefs.current.length) {
      return
    }

    // On load, set the active section based on the current scroll position
    handleScroll()

    // Add scroll event listener when the component mounts
    window.addEventListener('scroll', handleScroll)

    // Cleanup: Remove the listener when the component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  // Will scroll to a chosen section and update the active section
  const handleSectionChange = (sectionId = '') => {
    if (sectionId === '') {
      return
    }

    if (componentRef.current) {
      componentRef.current.querySelector(`#${sectionId}`).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  // Filter out sections with no title or anchorId
  sections = sections.filter((section) => {
    return section.title && section.anchorId
  })

  return (
    <>
      {/* Desktop sidebar */}
      <ul className="mr-m flex-col items-start gap-xxxs text-fg-title @m-up:ml-m ms:hidden ms-up:flex">
        {sections.map((section) => {
          const itemClass = activeSectionId === section.anchorId ? 'bg-theme-accent' : ''

          return (
            <li key={`list-${section.anchorId}`}>
              <a
                href={`#${section.anchorId}`}
                onClick={(e) => {
                  e.preventDefault()
                  handleSectionChange(section.anchorId)
                }}
                className={`inline-block rounded-sm p-xxs text-17 font-bold ${itemClass}`}>
                {section.title}
              </a>
            </li>
          )
        })}
      </ul>

      {/* Mobile sticky select */}
      <div className="-mt-px flex w-full gap-xs border-y bg-theme-accent p-s s:py-xxs ms-up:hidden">
        <span className="my-xxs flex items-center whitespace-nowrap border-r border-gradient pr-xs font-bold text-fg-title gradient-angle-[to_bottom]">
          Jump to
        </span>

        <select
          className="input--small"
          value={activeSectionId}
          onChange={(e) => handleSectionChange(e.target.value)}>
          {sections.map((section) => {
            return (
              <option key={`select-${section.anchorId}`} value={section.anchorId}>
                {section.title}
              </option>
            )
          })}
        </select>
      </div>
    </>
  )
}
